import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	useTransition,
} from "react"

import type { FilterOption } from "../../model/types"

interface UseControlledSelectProps {
	options: FilterOption[]
	value: string | number | undefined
	onChange: (value: string | number) => void
	disabled: boolean
	label: string
	dataCy: string
}

export const useControlledSelect = ({
	options,
	value,
	onChange,
	disabled,
	dataCy,
}: UseControlledSelectProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState("")
	const [deferredQuery, setDeferredQuery] = useState("")
	const [, startTransition] = useTransition()
	const containerRef = useRef<HTMLDivElement>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const listboxId = `${dataCy}-listbox`

	const selectedOption = useMemo(
		() => options.find(o => o.value === value),
		[options, value],
	)
	const selectedLabel = selectedOption?.label
	const selectedIcon = selectedOption?.customIcon

	const filteredOptions = useMemo(() => {
		const query = deferredQuery.toLowerCase()
		if (!query) return options

		return options
			.filter(option => option.label.toLowerCase().includes(query))
			.sort((a, b) => {
				const indexA = a.label.toLowerCase().indexOf(query)
				const indexB = b.label.toLowerCase().indexOf(query)
				return indexA - indexB
			})
	}, [options, deferredQuery])

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value
			setSearchQuery(val)
			startTransition(() => {
				setDeferredQuery(val)
			})
		},
		[startTransition],
	)

	const toggleDropdown = useCallback(() => {
		if (disabled) return
		setIsOpen(prev => {
			if (prev) {
				setSearchQuery("")
				setDeferredQuery("")
			}
			return !prev
		})
	}, [disabled])

	const closeDropdown = useCallback(() => {
		setIsOpen(false)
		setSearchQuery("")
		setDeferredQuery("")
		triggerRef.current?.focus()
	}, [])

	const handleSelect = useCallback(
		(optionValue: string | number) => {
			onChange(optionValue)
			closeDropdown()
		},
		[onChange, closeDropdown],
	)

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key !== "Escape") {
				return
			}

			event.stopPropagation()
			closeDropdown()
		},
		[closeDropdown],
	)

	useEffect(() => {
		if (isOpen && searchInputRef.current) {
			searchInputRef.current.focus()
		}
	}, [isOpen])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				event.target instanceof Node &&
				!containerRef.current.contains(event.target)
			) {
				setIsOpen(false)
				setSearchQuery("")
				setDeferredQuery("")
			}
		}

		document.addEventListener("mousedown", handleClickOutside)
		return () =>
			document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	return {
		isOpen,
		searchQuery,
		deferredQuery,
		filteredOptions,
		containerRef,
		searchInputRef,
		triggerRef,
		listboxId,
		selectedLabel,
		selectedIcon,
		toggleDropdown,
		closeDropdown,
		handleSearchChange,
		handleKeyDown,
		handleSelect,
	}
}
